import { ScreenBreakpoint } from 'apps/sigma2-ui/src/lib/hooks/useBreakpoints';
import { useBreakpoints } from 'apps/sigma2-ui/src/lib/hooks/useBreakpoints';
import React, { useMemo } from 'react';

export type PrimitiveItem =
  | string
  | number
  | boolean
  | null
  | undefined
  | React.ReactNode;

export type Responsive<T extends PrimitiveItem> =
  | T
  | [T, Partial<Record<ScreenBreakpoint, T>>];

export const fromResponsiveExtractDefault = <T extends PrimitiveItem>(
  responsive: Responsive<T>,
): T => {
  if (Array.isArray(responsive)) {
    return responsive[0];
  }

  return responsive;
};

export const fromResponsiveExtractBreakpoints = <T extends PrimitiveItem>(
  responsive: Responsive<T>,
): Partial<Record<ScreenBreakpoint, T>> => {
  if (Array.isArray(responsive)) {
    return responsive[1];
  }

  return {};
};

export const useResponsive = <T extends PrimitiveItem>(
  responsive: Responsive<T>,
): T => {
  const responsiveBreakpoints = fromResponsiveExtractBreakpoints(responsive);
  const defaultValue = fromResponsiveExtractDefault(responsive);

  const { isAboveOrEqualToBreakpoint } = useBreakpoints();

  return useMemo(() => {
    let currentValue: T = defaultValue;

    for (const [breakpointRaw, value] of Object.entries(
      responsiveBreakpoints,
    )) {
      const breakpoint = breakpointRaw as ScreenBreakpoint;
      if (isAboveOrEqualToBreakpoint(breakpoint)) {
        currentValue = value;
      }
    }

    return currentValue;
  }, [defaultValue, isAboveOrEqualToBreakpoint, responsiveBreakpoints]);
};
